import React, { useMemo, useState } from 'react';
import { formatFullDate } from '../../utils/forumDates';

interface CiaRdvModalProps {
  opponentDisplay: string;
  onClose: () => void;
  onSubmit: (rdvIso: string) => Promise<void>;
}

function pad2(n: number): string {
  return String(n).padStart(2, '0');
}

function defaultDateValue(): string {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

export default function CiaRdvModal({
  opponentDisplay,
  onClose,
  onSubmit,
}: CiaRdvModalProps) {
  const [dateValue, setDateValue] = useState(defaultDateValue);
  const [hour, setHour] = useState(() => {
    const h = new Date().getHours() + 1;
    return h >= 24 ? 20 : h;
  });
  const [minute, setMinute] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const preview = useMemo(() => {
    const iso = `${dateValue}T${pad2(hour)}:${pad2(minute)}:00`;
    const parsed = new Date(iso);
    if (Number.isNaN(parsed.getTime())) return null;
    return formatFullDate(parsed.toISOString());
  }, [dateValue, hour, minute]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    const iso = `${dateValue}T${pad2(hour)}:${pad2(minute)}:00`;
    const parsed = new Date(iso);
    if (Number.isNaN(parsed.getTime())) {
      setError('Date ou heure invalide');
      return;
    }
    if (parsed <= new Date()) {
      setError('Le rendez-vous doit être dans le futur');
      return;
    }
    setLoading(true);
    try {
      await onSubmit(parsed.toISOString());
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="cia-modal-overlay" role="presentation" onClick={onClose}>
      <div
        className="cia-modal"
        role="dialog"
        aria-labelledby="cia-rdv-title"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 id="cia-rdv-title" className="cia-modal-title">
          Proposer un RDV CIA
        </h3>
        <p className="cia-modal-subtitle">
          Adversaire : <strong>{opponentDisplay}</strong>
        </p>
        <form className="cia-rdv-form" onSubmit={handleSubmit}>
          <label className="cia-rdv-field">
            <span>Date</span>
            <input
              type="date"
              value={dateValue}
              onChange={(e) => setDateValue(e.target.value)}
              required
            />
          </label>
          <div className="cia-rdv-time-row">
            <label className="cia-rdv-field">
              <span>Heure</span>
              <select value={hour} onChange={(e) => setHour(Number(e.target.value))}>
                {Array.from({ length: 24 }, (_, i) => (
                  <option key={i} value={i}>{i} h</option>
                ))}
              </select>
            </label>
            <label className="cia-rdv-field">
              <span>Minutes</span>
              <select value={minute} onChange={(e) => setMinute(Number(e.target.value))}>
                {[0, 15, 30, 45].map((m) => (
                  <option key={m} value={m}>{pad2(m)}</option>
                ))}
              </select>
            </label>
          </div>
          {preview && (
            <p className="cia-rdv-preview">Rendez-vous proposé : {preview}</p>
          )}
          {error && <p className="cia-modal-error">{error}</p>}
          <div className="cia-modal-actions">
            <button type="button" className="cia-btn cia-btn--ghost" onClick={onClose} disabled={loading}>
              Annuler
            </button>
            <button type="submit" className="cia-btn cia-btn--primary" disabled={loading}>
              {loading ? 'Envoi…' : 'Envoyer la proposition'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
